import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import { VerifyEmail, ForgotPassword, ResetPassword, AdminRecovery } from "@/pages/auth/MiscAuth";

import CustomerLayout from "@/components/CustomerLayout";
import AdminLayout from "@/components/AdminLayout";

import Dashboard from "@/pages/customer/Dashboard";
import AddUsdt from "@/pages/customer/AddUsdt";
import SellUsdt from "@/pages/customer/SellUsdt";
import Wallet from "@/pages/customer/Wallet";
import Deposits from "@/pages/customer/Deposits";
import Orders from "@/pages/customer/Orders";
import OrderDetail from "@/pages/customer/OrderDetail";
import Transactions from "@/pages/customer/Transactions";
import Notifications from "@/pages/customer/Notifications";
import Support from "@/pages/customer/Support";
import Profile from "@/pages/customer/Profile";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminDeposits from "@/pages/admin/AdminDeposits";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminRates from "@/pages/admin/AdminRates";
import AdminAdjust from "@/pages/admin/AdminAdjust";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminOfficers from "@/pages/admin/AdminOfficers";
import AdminReset from "@/pages/admin/AdminReset";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
    </div>
  );
}

function Protected({ roles, children }) {
  const { user, checked } = useAuth();
  if (!checked || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  const isStaff = ["super_admin", "officer"].includes(user.role);
  if (roles === "staff" && !isStaff) return <Navigate to="/app/dashboard" replace />;
  if (roles === "customer" && isStaff) return <Navigate to="/admin/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin-recovery" element={<AdminRecovery />} />

        <Route path="/app" element={<Protected roles="customer"><CustomerLayout /></Protected>}>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="add-usdt" element={<AddUsdt />} />
          <Route path="sell" element={<SellUsdt />} />
          <Route path="wallet" element={<Wallet />} />
          <Route path="deposits" element={<Deposits />} />
          <Route path="orders" element={<Orders />} />
          <Route path="orders/:orderId" element={<OrderDetail />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="support" element={<Support />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        <Route path="/admin" element={<Protected roles="staff"><AdminLayout /></Protected>}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="deposits" element={<AdminDeposits />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:orderId" element={<AdminOrderDetail />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="rates" element={<AdminRates />} />
          <Route path="adjust" element={<AdminAdjust />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="tickets" element={<AdminTickets />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="officers" element={<AdminOfficers />} />
          <Route path="reset" element={<AdminReset />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
